import React, { useId } from "react";

export type CurrencyMode = "customer" | "base";

interface CurrencyToggleProps {
  /** Current toggle value: "customer" (default/unchecked) or "base" (checked) */
  value: CurrencyMode;
  /** Called when the user switches the toggle */
  onChange: (mode: CurrencyMode) => void;
  /** Label for the left (non-base) option. Defaults to "Customer Currency". */
  nonBaseLabel?: string;
  /** Label for the right (base) option. Defaults to "Base Currency (GBP)". */
  baseLabel?: string;
  /** Optional extra className for the container */
  className?: string;
  /** aria-label for the underlying checkbox input (overrides default) */
  "aria-label"?: string;
}

/**
 * CurrencyToggle
 *
 * A two-state labelled toggle built with Evo Design System classes:
 *   access-toggle-container  – outer wrapper
 *   access-toggle-active     – applied to the currently-active label
 *   access-toggle-switch     – wraps the hidden <input> + visible track
 *   access-toggle-slider     – the visible pill / track element
 *
 * Default (unchecked) = Customer Currency
 * Checked             = Base Currency (GBP)
 *
 * Fully keyboard-operable: Space / Enter toggles when the input is focused.
 */
export function CurrencyToggle({ value, onChange, nonBaseLabel = "Customer Currency", baseLabel = "Base Currency (GBP)", className, "aria-label": ariaLabel }: Readonly<CurrencyToggleProps>) {
  const inputId = useId();
  const isBase = value === "base";

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    onChange(e.target.checked ? "base" : "customer");
  }

  return (
    <div
      className={["access-toggle-container", className].filter(Boolean).join(" ")}
      /* Clicking the container label area also toggles — label association handles that */
    >
      {/* Left label – "Customer Currency" – active when NOT checked */}
      <label
        htmlFor={inputId}
        className={`access-toggle-active-label cursor-pointer${!isBase ? " access-toggle-active" : ""}`}
        aria-hidden="true"
      >
        {nonBaseLabel}
      </label>

      {/* Toggle switch */}
      <span className="access-toggle-switch">
        <input
          id={inputId}
          type="checkbox"
          role="switch"
          checked={isBase}
          onChange={handleChange}
          aria-label={ariaLabel ?? "Switch currency display mode"}
          aria-checked={isBase}
        />
        {/* Visible pill / slider – clicking it also fires the label's htmlFor link */}
        <label htmlFor={inputId} className="access-toggle-slider" aria-hidden="true">
          <span className="sr-only">Toggle currency mode</span>
        </label>
      </span>

      {/* Right label – "Base Currency (GBP)" – active when checked */}
      <label
        htmlFor={inputId}
        className={`access-toggle-active-label cursor-pointer${isBase ? " access-toggle-active" : ""}`}
        aria-hidden="true"
      >
        {baseLabel}
      </label>
    </div>
  );
}
